import {
  ArrowRight, ArrowUpRight, CalendarDays, Check, Facebook, Instagram, Mail,
  MapPin, Minus, Phone, Plus, Quote, Star, Users,
} from "lucide-react";
import ConceptCTAPill from "@/components/ConceptCTAPill";
import EventInquiryForm from "./EventInquiryForm";
import { BUSINESS } from "./historiaData";
import "./historia.css";

function InitialMark({ business }) {
  return <span className="ev-mark">{business.name.split(/\s|&/).filter(Boolean).slice(0, 3).map(word => word[0]).join("")}</span>;
}

function BusinessMark({ business }) {
  return business.media?.logo
    ? <img className="ev-logo" src={business.media.logo} alt={`${business.name} logo`} />
    : <InitialMark business={business} />;
}

function EventHeader({ business, links }) {
  return (
    <>
      <aside className="ev-disclaimer">Concept website created by <a href="https://mysitecare.eu/ph" target="_blank" rel="noopener noreferrer" className="underline decoration-white/40 hover:decoration-white">MySiteCare</a> — not currently operated by or affiliated with {business.name}.</aside>
      <header className="ev-header">
        <a href="#top" className="ev-brand"><BusinessMark business={business} /><span><strong>{business.name}</strong><small>{business.location}</small></span></a>
        <nav aria-label={`${business.name} concept navigation`}>
          {links.map(([label, href]) => <a key={href} href={href}>{label}</a>)}
          <a href="#inquire" className="ev-header__cta">Enquire <ArrowUpRight size={14} /></a>
        </nav>
      </header>
    </>
  );
}

function EventGallery({ business, variant, title = "Real events, real details." }) {
  if (!business.media?.gallery?.length) return null;
  return (
    <section className={`ev-gallery ev-gallery--${variant}`} id="work">
      <header><p className="ev-kicker">From the real portfolio</p><h2>{title}</h2><RealWorkLink business={business} label="See the latest work" /></header>
      <div>{business.media.gallery.map((src, index) => (
        <figure key={src}><img src={src} alt={`${business.name} real event work ${index + 1}`} loading="lazy" decoding="async" /></figure>
      ))}</div>
    </section>
  );
}

function RealWorkLink({ business, label = "See real event work" }) {
  const href = business.contact.instagram || business.contact.facebook || business.sources[0];
  return <a className="ev-real-work" href={href} target="_blank" rel="noopener noreferrer">{label}<ArrowUpRight size={15} /></a>;
}

function Stars({ count }) {
  if (!count) return null;
  return <span className="ev-stars" aria-label={`${count} out of 5 stars`}>{Array.from({ length: count }, (_, index) => <Star key={index} size={13} fill="currentColor" />)}</span>;
}

function ReviewStories({ business, style = "cards" }) {
  return (
    <section className={`ev-reputation ev-reputation--${style}`} id="stories">
      <div className="ev-reputation__heading"><p className="ev-kicker">Client evidence</p><h2>{business.reviews.length ? "What the experience felt like." : "A reputation worth making easier to find."}</h2><p>{business.reviewSnapshot}</p></div>
      {business.reviews.length > 0 && <div className="ev-reputation__list">{business.reviews.map((item, index) => (
        <article key={`${item.author}-${index}`}>
          <Quote aria-hidden="true" />
          <p>{item.summary}</p>
          <footer><div><strong>{item.author}</strong><span>{item.context}</span></div><Stars count={item.explicitStars} /><a href={item.source} target="_blank" rel="noopener noreferrer" aria-label="Open the original public source"><ArrowUpRight size={14} /></a></footer>
        </article>
      ))}</div>}
      {!business.reviews.length && <RealWorkLink business={business} label="Visit the public business page" />}
    </section>
  );
}

function ContactAndSources({ business }) {
  const map = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(business.address)}`;
  return (
    <>
      <section className="ev-contact" id="contact">
        <div><p className="ev-kicker">Verified contact routes</p><h2>Continue with {business.shortName} directly.</h2><p>Availability, current packages, prices, inclusions and policies must be confirmed with the business.</p></div>
        <div className="ev-contact__links">
          {business.contact.phone && <a href={`tel:${business.contact.phoneHref}`}><Phone size={17} /><span><small>Call</small>{business.contact.phone}</span></a>}
          {business.contact.email && <a href={`mailto:${business.contact.email}`}><Mail size={17} /><span><small>Email</small>{business.contact.email}</span></a>}
          {business.contact.facebook && <a href={business.contact.facebook} target="_blank" rel="noopener noreferrer"><Facebook size={17} /><span><small>Social</small>Facebook</span><ArrowUpRight size={14} /></a>}
          {business.contact.instagram && <a href={business.contact.instagram} target="_blank" rel="noopener noreferrer"><Instagram size={17} /><span><small>Social</small>Instagram</span><ArrowUpRight size={14} /></a>}
          <a href={map} target="_blank" rel="noopener noreferrer"><MapPin size={17} /><span><small>Location</small>{business.address}</span><ArrowUpRight size={14} /></a>
        </div>
      </section>
      <details className="ev-sources"><summary>Information sources and accuracy note <span>{business.sources.length} sources</span></summary><p>Details last checked in August 2026. Confirm changing details directly with the business. Community experiences are labelled separately from explicit platform ratings.</p><ul>{business.sources.map((source, index) => <li key={source}><span>0{index + 1}</span><a href={source} target="_blank" rel="noopener noreferrer">Open source <ArrowUpRight size={12} /></a></li>)}</ul></details>
      <footer className="ev-footer"><div className="ev-brand"><BusinessMark business={business} /><span><strong>{business.name}</strong><small>{business.location}</small></span></div><p>Unsolicited concept by <a href="https://mysitecare.eu/ph" target="_blank" rel="noopener noreferrer">MySiteCare</a>. No affiliation implied.</p></footer>
    </>
  );
}

export default function HistoriaSite() {
  const business = BUSINESS;
  return (
    <main className={`ev-site ev-site--${business.layout}`} data-testid={`event-site-${business.slug}`}>
      <>
    <EventHeader business={business} links={[["Celebrations", "#celebrations"], ["Since 2018", "#story"], ["Experience", "#stories"]]} />
    <section className="hi-hero" id="top"><div className="hi-hero__year">2018<span>—</span>2026</div><div><p className="ev-kicker">{business.eyebrow}</p><h1>{business.headline}</h1><p>{business.intro}</p><RealWorkLink business={business} /></div></section>
    <section className="hi-celebrations" id="celebrations"><h2>Every room has a different energy.</h2><div>{business.services.map((item, index) => <article key={item}><span>0{index + 1}</span><h3>{item}</h3></article>)}</div></section>
    <section className="hi-story" id="story"><span>Since 2018</span><div><p className="ev-kicker">The public story</p><h2>Well-planned, meaningful events.</h2><p>{business.story}</p><p>Coordinator {business.lead} is one of the public faces associated with Historia's event work.</p></div></section>
    <ReviewStories business={business} style="dark" /><EventInquiryForm business={business} title="Give the celebration a clear direction." /><ContactAndSources business={business} />
  </>
      <ConceptCTAPill slug={business.slug} />
    </main>
  );
}
